import React from 'react';
import styled from 'styled-components';
import uuid from 'uuid';
import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import colors from 'theme/colors';
import InfoCircle from '../../icons/info-circle';

import {
  REVENUE,
  BASE_PRICE_FORMULA,
  CAPS,
  FLOORS,
  REIMBURSED_SUPPLY_COST,
  NETBACK,
  PUSH,
  PULL,
  EXTENSION,
  EARLY_TERMINATION,
  VARIABLE_SUPPLY_COST,
  ANNUAL_FIXED_SUPPLY_COST,
  CAPITAL_COST_INVENTORY,
  CAPITAL_COST_TRANSIT_INVENTORY,
  CAPITAL_COST_PAYMENT,
  EXPECTED_COST,
  MARGIN,
  VARIABLE_MANUFACTURING_COST,
  COST_TO_SERVE_YIELD,
  EXCLUDING_TEXT,
  VARIABLE_FIXED_TEXT,
} from './constants';

export interface CostToServeInterface {
  mainHeader: string;
  header: string;
  coloredNegData?: Array<string>;
  posData?: Array<string>;
  negData?: Array<string>;
  parallelData?: string;
  text?: string;
}

const { white, darkBackgroundElevation1, lightYellow, darkBackgroundBase, labelPlatinum } = colors;

const CostContainer = styled.div`
  border-radius: 3px;
  color: ${white};
  padding: 24px 24px 24px 29px;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 250px 400px 245px 236px;
  font-weight: 500;
`;

const MainHeaderTite = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 8px;
`;

const HeaderAndData = styled.div`
  background: ${darkBackgroundElevation1};
  border-radius: 2px;
  padding: 8px 5px;
`;

const FontText = styled.div`
  font-size: 12px; //
  line-height: 14px;
  padding: 8px;
`;
const HeaderContent = styled(FontText)`
  display: flex;
`;
const ParalleData = styled.div`
  font-size: 12px;
  color: #ff9400;
  padding-left: 4px;
`;
const TitleData = styled(FontText)`
  padding: 3px;
  &:before {
    content: '+';
    font-size: 16px;
    margin-right: 2px;
    font-weight: 500;
  }
  &:last-child {
    margin-bottom: 6px;
  }
`;

const NegativeTitle = styled(FontText)`
  padding: 3px;
  &:before {
    content: '—';
    margin: 2px 3px;
    font-size: 16px;
    font-weight: 500;
  }
`;

const ColouredNegativeText = styled.div`
  font-size: 12px; //
  line-height: 14px;
  color: #FF9400; //${lightYellow};
  padding: 3px;
  &:before {
    content: '—';
    font-size: 18px;
    font-weight: 500;
    margin-left: 7px;
    margin-right: 3px;
  }
`;
const BorderTop = styled.div`
  border-top: 0.5px solid ${white};
  margin: 0px 5px;
  padding-top: 8px;
`;

const ThickBorder = styled.div`
  border-top: 3px solid ${white};
  margin: 10px -1px;
`;
const WrapText = styled(HeaderAndData)`
  margin-top: 12px;
  font-size: 12px; //
  line-height: 14px;
  padding-left: 16px;
  margin-left: 5px;
  margin-right: 5px;
`;

const Info = styled.span<{ top: number }>`
  position: relative;
  left: 5px;
  top: ${props => props.top}px;
`;

const useStyles = makeStyles({
  noMaxWidth: {
    backgroundColor: darkBackgroundBase,
    color: labelPlatinum,
    maxWidth: '100%',
    marginTop: '100px',
    marginRight: '100px',
    marginLeft: '200px',
  },
});

const res = [
  {
    mainHeader: REVENUE,
    header: BASE_PRICE_FORMULA,
    posData: [CAPS, FLOORS, REIMBURSED_SUPPLY_COST],
  },
  {
    mainHeader: NETBACK,
    header: REVENUE,
    posData: [PUSH, PULL, EXTENSION, EARLY_TERMINATION],
    negData: [VARIABLE_SUPPLY_COST, ANNUAL_FIXED_SUPPLY_COST],
    coloredNegData: [CAPITAL_COST_INVENTORY, CAPITAL_COST_TRANSIT_INVENTORY, CAPITAL_COST_PAYMENT, EXPECTED_COST],
  },
  {
    mainHeader: MARGIN,
    header: NETBACK,
    negData: [VARIABLE_MANUFACTURING_COST],
  },
  {
    mainHeader: COST_TO_SERVE_YIELD,
    header: MARGIN,
    parallelData: EXCLUDING_TEXT,
    text: VARIABLE_FIXED_TEXT,
  },
];

const CostToServeInfo = ({
  mainHeader,
  header,
  posData,
  negData,
  coloredNegData,
  parallelData,
  text,
}: CostToServeInterface) => (
  <div key={uuid()}>
    <MainHeaderTite>{mainHeader}</MainHeaderTite>
    <HeaderAndData>
      <HeaderContent>
        <div>{header}</div>
        <ParalleData>{parallelData}</ParalleData>
      </HeaderContent>
      {posData && (
        <BorderTop>
          {posData.map(dt => (
            <TitleData key={dt}>{dt}</TitleData>
          ))}
        </BorderTop>
      )}
      {negData && (
        <BorderTop>
          {negData.map(nd => (
            <NegativeTitle key={nd}>{nd}</NegativeTitle>
          ))}
        </BorderTop>
      )}
      {coloredNegData?.map(cng => (
        <ColouredNegativeText key={cng}>{cng}</ColouredNegativeText>
      ))}
    </HeaderAndData>
    {text && (
      <ThickBorder>
        <WrapText>{text}</WrapText>
      </ThickBorder>
    )}
  </div>
);

export const CashCostToServeDetails = () => (
  <CostContainer>
    {res.map(({ mainHeader, header, posData, negData, parallelData, coloredNegData, text }) => (
      <CostToServeInfo
        mainHeader={mainHeader}
        header={header}
        posData={posData}
        negData={negData}
        parallelData={parallelData}
        coloredNegData={coloredNegData}
        key={uuid()}
        text={text}
      />
    ))}
  </CostContainer>
);

const CostToServeTooltip = (props: { top?: number }) => {
  const classes = useStyles();
  const { top } = props;
  const topLimit = 5;
  return (
    <Tooltip title={<CashCostToServeDetails />} classes={{ tooltip: classes.noMaxWidth }}>
      <Info top={top ?? topLimit}>
        <InfoCircle width={20} height={20} />
      </Info>
    </Tooltip>
  );
};

export default CostToServeTooltip;
